import Object3D from "_src/engine/object";
import { vec2, vec3, vec4, mat4 } from "_src/libs/gl-matrix";
import { cameraType } from "_src/engine/constants";
import { normalizePlane } from "_src/engine/math";

const _vec4Tmp1 = vec4.create();

const _mat4Tmp1 = mat4.create();

export default class Camera extends Object3D {
  constructor(type) {

    super();

    this.isCamera = true;

    this.type = type ? type : cameraType.PERSPECTIVE;

    this.near = 0.1;
    this.far  = 1000.0;
    this.nearFar = vec2.fromValues(this.near, this.far);
    this.aspect = 1.0;
    this.projectionShift = vec2.fromValues(0, 0);

    this.fixedFov = Math.PI / 3;

    this._calculateAdaptiveFov();

    this.left = -1.0;
    this.right = 1.0;
    this.top = 1.0;
    this.bottom = -1.0;

    this.frustum = [
      [0,0,0,0], // left
      [0,0,0,0], // right
      [0,0,0,0], // top
      [0,0,0,0], // bottom
      [0,0,0,0], // near
      [0,0,0,0]  // far
    ];

    this.viewProjMat    = mat4.create();
    this.projMat        = mat4.create();
    this.viewMat        = mat4.create();
    this.viewInvMat     = mat4.create();

    this.viewport = vec4.fromValues(0,0,1,1);
    this.texelSize = vec2.fromValues(1,1);

  }

  setViewport(width, height, left = 0, bottom = 0) {

    this.viewport[0] = left;
    this.viewport[1] = bottom;
    this.viewport[2] = width;
    this.viewport[3] = height;

    this.texelSize[0] = 1.0 / width;
    this.texelSize[1] = 1.0 / height;

    this.aspect = width / height;

    this._calculateAdaptiveFov();
    this.updateProjectMatrix();

  }

  setFieldOfView(angle) {
    this.fixedFov = angle;
    this._calculateAdaptiveFov();
  }

  setFovY(angle) {
    this.fov = angle;
    this._calculateFixedFov();
  }

  setType(type) {

    this.type = type;

    this.updateProjectMatrix();

  }

  isPerspective() {
    return this.type == cameraType.PERSPECTIVE;
  }

  isOrtho() {
    return this.type == cameraType.ORTHO;
  }

  updateProjectMatrix() {

    if (this.isPerspective()) {
      this.calculatePerspectiveProjMat(this.projMat);
    } else {
      this.calculateOrthoProjMat(this.projMat);
    }

    this.setProjectionMatrix(this.projMat);

  }

  setProjectionMatrix(projMat) {

    mat4.copy(this.projMat, projMat);
    mat4.multiply(this.viewProjMat, this.projMat, this.viewMat);

    vec2.set(this.nearFar, this.near, this.far);

    this.extractFrustum();

  }

  calculatePerspectiveProjMat(matrix) {
    mat4.perspective(matrix, this.fov, this.aspect, this.near, this.far);
    matrix[8] = this.projectionShift[0];
    matrix[9] = this.projectionShift[1];

    return matrix;
  }

  calculateOrthoProjMat(matrix) {

    const verticalDiff = (this.top - this.bottom) * this.aspect;

    this.left = -0.5 * verticalDiff;
    this.right = 0.5 * verticalDiff;

    return mat4.ortho(matrix, this.left, this.right, this.bottom, this.top,
                this.near, this.far);

  }

  calculateRay(x, y, dest) {

    if (this.isPerspective()) {
      return this._calculateRayPersp(x, y, dest);
    } else {
      return this._calculateRayOrtho(dest);
    }

  }

  extractFrustum() {

    const m = this.viewProjMat;

    const left   = this.frustum[0];
    const right  = this.frustum[1];
    const top    = this.frustum[2];
    const bottom = this.frustum[3];
    const near   = this.frustum[4];
    const far    = this.frustum[5];

    left[0] = -m[3] - m[0];
    left[1] = -m[7] - m[4];
    left[2] = -m[11] - m[8];
    left[3] = -m[15] - m[12];

    right[0] = m[0] - m[3];
    right[1] = m[4] - m[7];
    right[2] = m[8] - m[11];
    right[3] = m[12] - m[15];

    top[0] = m[1] - m[3];
    top[1] = m[5] - m[7];
    top[2] = m[9] - m[11];
    top[3] = m[13] - m[15];

    bottom[0] = -m[3] - m[1];
    bottom[1] = -m[7] - m[5];
    bottom[2] = -m[11] - m[9];
    bottom[3] = -m[15] - m[13];

    near[0] = -m[3] - m[2];
    near[1] = -m[7] - m[6];
    near[2] = -m[11] - m[10];
    near[3] = -m[15] - m[14];

    far[0] = m[2] - m[3];
    far[1] = m[6] - m[7];
    far[2] = m[10] - m[11];
    far[3] = m[14] - m[15];

    normalizePlane(left);
    normalizePlane(right);
    normalizePlane(top);
    normalizePlane(bottom);
    normalizePlane(near);
    normalizePlane(far);

  }

  projectPoint(x, y, z, dest) {

    const point = vec4.set(_vec4Tmp1, x, y, z, 1);

    vec4.transformMat4(point, point, this.viewProjMat);
    vec3.scale(point, point, 1.0 / point[3]);

    return vec3.copy(dest, point);

  }

  unprojectPoint(x, y, z, dest) {

    const point = vec4.set(_vec4Tmp1, x, y, z, 1);
    const invertedViewProject = mat4.invert(_mat4Tmp1, this.viewProjMat);

    vec4.transformMat4(point, point, invertedViewProject);
    vec3.scale(point, point, 1.0 / point[3]);

    return vec3.copy(dest, point);

  }

  _calculateAdaptiveFov() {
    if (this.aspect > 1) {
      this.fov = 2 * Math.atan(Math.tan(this.fixedFov*0.5) / this.aspect);
    } else {
      this.fov = this.fixedFov;
    }
  }

  _calculateFixedFov() {
    if (this.aspect > 1) {
      this.fixedFov = Math.atan(Math.tan(this.fov / 2) * this.aspect) * 2;
    } else {
      this.fixedFov = this.fov;
    }
  }

  _calculateRayPersp(x, y, dest) {

    const tanFOV = Math.tan(this.fov * 0.5);

    const locX = x * tanFOV * this.aspect;
    const locY = y * tanFOV;

    const ray = vec4.set(_vec4Tmp1, locX, locY, -1, 0);

    vec4.transformMat4(ray, ray, this.modelMatrix);

    return vec3.normalize(dest, ray);

  }

  _calculateRayOrtho(dest) {

    const ray = vec4.set(_vec4Tmp1, 0, 0, -1, 0);

    vec4.transformMat4(ray, ray, this.modelMatrix);

    return vec3.normalize(dest, ray);

  }

  calculateOrigin(x, y, dest) {

    if (this.isPerspective()) {
      return this._calculateOriginPersp(dest);
    } else {
      return this._calculateOriginOrtho(x, y, dest);
    }

  }

  _calculateOriginPersp(dest) {
    return vec3.copy(dest, this.trans);
  }

  _calculateOriginOrtho(x, y, dest) {

    const xLocal = x * this.top * this.aspect;
    const yLocal = y * this.right / this.aspect;

    const origin = vec4.set(_vec4Tmp1, xLocal, yLocal, 0, 1);

    vec4.transformMat4(dest, origin, this.modelMatrix);

    return dest;

  }

  toScreenSpace(point3D, dest) {
    this.updateMatrix();
    return vec3.transformMat4(dest, point3D, this.viewProjMat);
  }

  updateMatrix() {

    const needsMatUpdate = this.checkNeedsMatUpdate();

    super.updateMatrix();

    if (needsMatUpdate) {

      mat4.invert(this.viewMat, this.modelMatrix);
      mat4.multiply(this.viewProjMat, this.projMat, this.viewMat);

      this.extractFrustum();

    }

  }

  updateNearFar(near, far) {

    this.near = near;
    this.far = far;

    this.updateProjectMatrix();

  }
}
